import React, { useCallback, useRef, useState } from "react";

import { ClockCircleFilled } from "@ant-design/icons";
import { Tooltip } from "antd";

import { SPEC_FLAGS_MAP } from "../../../Common/constants/SpecFlagConstants";
import StyledTooltip from "../../../Common/UIComponents/StyledTooltip";
import LockedValue from "../../../grace-period/components/LockedValue";
import { useAccessStatus } from "../../../grace-period/hooks/useAccessStatus";
import { VALID_SPECS_SET } from "../Components/ReportSpecs/helper";
import { formatSpecAlertLabel, getReportAlertRemainingDisplay } from "./alertRemainingUtils";
import ReportsTableSpecTooltip from "./ReportsTableSpecTooltip";

import { ReactComponent as InfoIcon } from "../../../../assets/images/InfoBlue.svg";

import "./ReportsTableSpecCell.css";

const PARTIAL_REPORT_HINT = "Tests are in progress. A partial report is available.";
const IN_PROCESS_SPEC_FLAGS = new Set(["0", "4"]);
const specTooltipCache = new Map();

const getSpecStatusLabel = (specsFlag, report) => {
  const { label, altLabel } = SPEC_FLAGS_MAP[specsFlag];
  return formatSpecAlertLabel(specsFlag, altLabel ?? label, report);
};

const handlePartialReportHintClick = (event) => {
  event.stopPropagation();
};

export default function ReportsTableSpecCell({
  rowData,
  enableSpecHoverTooltip,
  fetchSpecTooltipData,
}) {
  const { areResultsLocked } = useAccessStatus();
  const [tooltipLoading, setTooltipLoading] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const loadingRef = useRef(false);

  const specs_flag = rowData.getValue();
  const isValidSpec = specs_flag?.toString() && VALID_SPECS_SET.has(specs_flag.toString());
  const report = rowData.row.original;
  const showLockedStatus = areResultsLocked && isValidSpec
    && !IN_PROCESS_SPEC_FLAGS.has(specs_flag.toString());

  const loadTooltipData = useCallback(async () => {
    if (!fetchSpecTooltipData || loadingRef.current) {
      return;
    }

    const cacheKey = `${report.report_id}_${report.received_date}_${report.sample_id}`;
    if (specTooltipCache.has(cacheKey)) {
      setTestResults(specTooltipCache.get(cacheKey));
      return;
    }

    loadingRef.current = true;
    setTooltipLoading(true);
    try {
      const results = await fetchSpecTooltipData(report);
      specTooltipCache.set(cacheKey, results);
      setTestResults(results);
    } finally {
      loadingRef.current = false;
      setTooltipLoading(false);
    }
  }, [fetchSpecTooltipData, report]);

  const handleOpenChange = (open) => {
    if (open) {
      loadTooltipData();
    }
  };

  const cellContent = isValidSpec ? (
    <div className="ReportsTableSpecCell__Content">
      <img
        className="ReportsTableSpecCell__Icon"
        src={SPEC_FLAGS_MAP[specs_flag].icon}
        alt="specFlagIcon"
      />
      <span
        className="ReportsTableSpecCell__Status"
        style={{
          color: SPEC_FLAGS_MAP[specs_flag].color,
        }}
      >
        {getSpecStatusLabel(specs_flag, report)}
      </span>
    </div>
  ) : null;

  if (showLockedStatus) {
    return (
      <div className="ReportsTableSpecCell">
        <LockedValue locked blur={false} />
      </div>
    );
  }

  if (!cellContent) {
    return null;
  }

  const showPartialReportHint = Boolean(report?.partial_report_generated)
    && IN_PROCESS_SPEC_FLAGS.has(specs_flag.toString());

  const alertRemainingDisplay = getReportAlertRemainingDisplay(report);
  const specContent = enableSpecHoverTooltip && !areResultsLocked ? (
    <StyledTooltip
      title={(
        <ReportsTableSpecTooltip
          testResults={testResults}
          loading={tooltipLoading}
        />
      )}
      placement="topRight"
      onOpenChange={handleOpenChange}
      className="ReportsTableSpecCell__Tooltip"
      variant="light"
      getPopupContainer={() => document.body}
    >
      <div className="ReportsTableSpecCell__HoverTarget">
        {cellContent}
      </div>
    </StyledTooltip>
  ) : cellContent;

  return (
    <div className="ReportsTableSpecCell">
      <div className="ReportsTableSpecCell__Primary">
        {specContent}
        {showPartialReportHint && (
          <Tooltip
            title={PARTIAL_REPORT_HINT}
            placement="bottom"
            classNames={{ root: "ReportsTableSpecCell__DarkTooltip" }}
            getPopupContainer={() => document.body}
          >
            <span
              className="ReportsTableSpecCell__PartialHint"
              onClick={handlePartialReportHintClick}
            >
              <InfoIcon
                className="ReportsTableSpecCell__PartialHintIcon"
                aria-label={PARTIAL_REPORT_HINT}
              />
            </span>
          </Tooltip>
        )}
      </div>
      {alertRemainingDisplay?.label && (
        <Tooltip
          title={alertRemainingDisplay.tooltipTitle}
          placement="bottom"
          classNames={{ root: "ReportsTableSpecCell__DarkTooltip ReportsTableSpecCell__AlertTooltip" }}
          getPopupContainer={() => document.body}
        >
          <div className="ReportsTableSpecCell__AlertRemaining">
            <span className="ReportsTableSpecCell__AlertRemainingIconWrap">
              <ClockCircleFilled className="ReportsTableSpecCell__AlertRemainingIcon" aria-hidden />
            </span>
            <span className="ReportsTableSpecCell__AlertRemainingText">
              {alertRemainingDisplay.label}
            </span>
          </div>
        </Tooltip>
      )}
    </div>
  );
}
